import { Link } from 'react-router-dom';
import { Modal } from '../ui/Modal';
import type { CitStatusDetailRow, CitUnpaidDepositDetail } from '../../lib/citList';
import { citDocumentStatusClass } from '../../lib/citList';

export type CitSummaryModalKind =
  | 'all'
  | 'New'
  | 'Processing'
  | 'Completed'
  | 'Cancelled'
  | 'unpaid';

type Props = {
  kind: CitSummaryModalKind | null;
  unpaidRows: CitUnpaidDepositDetail[];
  statusRows: CitStatusDetailRow[];
  detailPath: (submissionId: string) => string;
  onClose: () => void;
};

function titleFor(kind: CitSummaryModalKind): string {
  if (kind === 'unpaid') return 'Unpaid deposits';
  if (kind === 'all') return 'All entries';
  return `${kind} entries`;
}

function CustomerCell({ code, name }: { code: string; name: string }) {
  if (!code && !name) return <span>—</span>;
  return (
    <span className="cit-summary-modal__customer">
      {code ? <span className="mono">{code}</span> : null}
      {name ? <span>{name}</span> : null}
    </span>
  );
}

export function CitSummaryDetailModal({
  kind,
  unpaidRows,
  statusRows,
  detailPath,
  onClose,
}: Props) {
  const open = kind != null;
  const title = kind ? titleFor(kind) : '';

  return (
    <Modal title={title} open={open} onClose={onClose} wide>
      {kind === 'unpaid' ? (
        unpaidRows.length === 0 ? (
          <p className="field-hint">No unpaid deposits.</p>
        ) : (
          <div className="cit-summary-modal__list">
            {unpaidRows.map((row) => (
              <article key={row.key} className="cit-summary-modal__card">
                <div className="cit-summary-modal__card-head">
                  <div>
                    <div className="cit-summary-modal__eyebrow">Deposit no.</div>
                    <div className="cit-summary-modal__deposit mono">{row.depositNo}</div>
                    {row.invoiceAmount ? (
                      <div className="cit-summary-modal__meta">Amount: {row.invoiceAmount}</div>
                    ) : null}
                  </div>
                  <Link
                    to={detailPath(row.submissionId)}
                    className="btn-secondary btn-sm"
                    onClick={onClose}
                  >
                    Open
                  </Link>
                </div>
                <dl className="cit-summary-modal__facts">
                  <div>
                    <dt>Sales confirmation</dt>
                    <dd className="mono">{row.salesConfirmationNo}</dd>
                  </div>
                  <div>
                    <dt>Customer</dt>
                    <dd>
                      <CustomerCell code={row.customerCode} name={row.customerName} />
                    </dd>
                  </div>
                </dl>
                <div className="cit-summary-modal__items">
                  <div className="cit-summary-modal__eyebrow">Items</div>
                  {row.items.length === 0 ? (
                    <p className="field-hint">No items tagged on this deposit.</p>
                  ) : (
                    <ul>
                      {row.items.map((item, index) => (
                        <li key={`${row.key}:${item.itemNo}:${index}`}>
                          <span className="mono">{item.itemNo}</span>
                          {item.description ? (
                            <span className="cit-summary-modal__item-desc">{item.description}</span>
                          ) : null}
                          <span className="cit-summary-modal__item-qty">
                            Qty {item.quantity}
                            {item.uom ? ` ${item.uom}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            ))}
          </div>
        )
      ) : statusRows.length === 0 ? (
        <p className="field-hint">No entries in this group.</p>
      ) : (
        <div className="cit-summary-modal__list">
          {statusRows.map((row) => (
            <article key={row.key} className="cit-summary-modal__card">
              <div className="cit-summary-modal__card-head">
                <div>
                  <div className="cit-summary-modal__eyebrow">Sales confirmation</div>
                  <div className="cit-summary-modal__deposit mono">{row.salesConfirmationNo}</div>
                  <div className="cit-summary-modal__status-wrap">
                    <span className={citDocumentStatusClass(row.status)}>{row.status}</span>
                  </div>
                </div>
                <Link
                  to={detailPath(row.submissionId)}
                  className="btn-secondary btn-sm"
                  onClick={onClose}
                >
                  Open
                </Link>
              </div>
              <dl className="cit-summary-modal__facts">
                <div>
                  <dt>Customer</dt>
                  <dd>
                    <CustomerCell code={row.customerCode} name={row.customerName} />
                  </dd>
                </div>
              </dl>
              <div className="cit-summary-modal__items">
                <div className="cit-summary-modal__eyebrow">Items</div>
                {row.items.length === 0 ? (
                  <p className="field-hint">No items.</p>
                ) : (
                  <ul>
                    {row.items.map((item) => (
                      <li key={`${row.key}:${item.itemNo}`}>
                        <span className="mono">{item.itemNo}</span>
                        {item.description ? (
                          <span className="cit-summary-modal__item-desc">{item.description}</span>
                        ) : null}
                        <span className="cit-summary-modal__item-qty">
                          SOC {item.socQty}
                          {item.uom ? ` ${item.uom}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </Modal>
  );
}
